import { db } from '../lib/db';
import type { StudyGroup, StudySession, SharedNote } from '../lib/db';
import { supabase } from '../lib/supabase';

export const studyGroupService = {
  // Study Group Operations
  async createGroup(name: string, description: string, userId: string, isPublic: boolean = true): Promise<StudyGroup> {
    const group: StudyGroup = {
      name,
      description,
      createdBy: userId,
      createdAt: new Date(),
      members: [userId],
      isPublic
    };

    const id = await db.studyGroups.add(group);
    return { ...group, id };
  },

  async getGroup(groupId: number): Promise<StudyGroup | undefined> {
    return await db.studyGroups.get(groupId);
  },

  async getUserGroups(userId: string): Promise<StudyGroup[]> {
    return await db.studyGroups
      .where('members')
      .contains(userId)
      .toArray();
  },

  async joinGroup(groupId: number, userId: string): Promise<void> {
    const group = await this.getGroup(groupId);
    if (!group) throw new Error('Group not found');
    
    if (!group.members.includes(userId)) {
      group.members.push(userId);
      await db.studyGroups.put(group);
    }
  },

  async leaveGroup(groupId: number, userId: string): Promise<void> {
    const group = await this.getGroup(groupId);
    if (!group) throw new Error('Group not found');
    
    group.members = group.members.filter(id => id !== userId);
    await db.studyGroups.put(group);
  },

  // Study Session Operations
  async createSession(session: Omit<StudySession, 'id' | 'createdAt'>): Promise<StudySession> {
    const newSession: StudySession = {
      ...session,
      createdAt: new Date()
    };

    const id = await db.studySessions.add(newSession);
    return { ...newSession, id };
  },

  async getGroupSessions(groupId: number): Promise<StudySession[]> {
    return await db.studySessions
      .where('groupId')
      .equals(groupId)
      .toArray();
  },

  async getUpcomingSessions(userId: string): Promise<StudySession[]> {
    const now = new Date();
    return await db.studySessions
      .where('participants')
      .contains(userId)
      .filter(session => session.scheduledFor > now)
      .sortBy('scheduledFor');
  },

  // Shared Notes Operations
  async shareNote(note: Omit<SharedNote, 'id' | 'sharedAt'>): Promise<SharedNote> {
    const sharedNote: SharedNote = {
      ...note,
      sharedAt: new Date(),
      reactions: {},
      comments: []
    };

    const id = await db.sharedNotes.add(sharedNote);
    return { ...sharedNote, id };
  },

  async getGroupNotes(groupId: number): Promise<SharedNote[]> {
    return await db.sharedNotes
      .where('groupId')
      .equals(groupId)
      .toArray();
  },

  async addComment(noteId: number, userId: string, content: string): Promise<void> {
    const note = await db.sharedNotes.get(noteId);
    if (!note) throw new Error('Note not found');

    note.comments.push({
      id: crypto.randomUUID(),
      userId,
      content,
      createdAt: new Date()
    });

    await db.sharedNotes.put(note);
  },

  async addReaction(noteId: number, userId: string, reaction: string): Promise<void> {
    const note = await db.sharedNotes.get(noteId);
    if (!note) throw new Error('Note not found');

    note.reactions[userId] = reaction;
    await db.sharedNotes.put(note);
  },

  // Sync with server
  async syncWithServer(): Promise<void> {
    // TODO: Implement sync logic with Supabase
    // This will sync study groups, sessions, and shared notes
    // between local IndexedDB and Supabase
  }
};
